import React, { useEffect, useState } from 'react';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import './ModalForms.css';
import '../common/IconButton.css';
import { IconTrash } from '../common/Icons';

const emptyDeposit = {
  date: '',
  value: '',
  currency: '$',
  type: '',
  receipt: ''
};

const DEPOSIT_TYPE_OPTIONS = [
  { value: 'tranferencia', label: 'Transferencia' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'Pago directo del pasajero', label: 'Pago directo del pasajero' },
];

const normalizeDepositType = (type) => {
  const raw = String(type || '').trim();
  const t = raw.toLowerCase();
  if (!t) return '';
  // DB enum historically uses a typo: "tranferencia"
  if (t === 'transferencia' || t === 'tranferencia') return 'tranferencia';
  if (t === 'efectivo') return 'efectivo';
  if (t === 'pago directo del pasajero') return 'Pago directo del pasajero';
  return raw;
};

const DepositModal = ({ isOpen, onClose, onSave, onDelete, initialData, serviceCurrency }) => {
  const [formData, setFormData] = useState(emptyDeposit);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const effectiveCurrency = serviceCurrency || initialData?.currency || '$';
    if (initialData) {
      setFormData({
        date: initialData.date ? initialData.date.slice(0, 10) : '',
        value: initialData.value ?? '',
        currency: effectiveCurrency,
        type: normalizeDepositType(initialData.type),
        receipt: initialData.receipt || ''
      });
    } else {
      const today = new Date().toISOString().slice(0, 10);
      setFormData({ ...emptyDeposit, date: today, currency: effectiveCurrency });
    }
  }, [initialData, isOpen, serviceCurrency]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (!formData?.type) {
        setError('Seleccioná un tipo de pago.');
        return;
      }
      await onSave(formData);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Error al guardar la orden de pago');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={initialData ? 'Editar Orden de Pago' : 'Nueva Orden de Pago'}
      isOpen={isOpen}
      onClose={onClose}
      onEnter={() => handleSubmit()}
      footer={(
        <div className="modal-footer">
          <div className="footer-left">
            {initialData && onDelete && (
              <button
                type="button"
                className="icon-btn icon-btn--delete"
                onClick={() => setConfirmOpen(true)}
                disabled={saving}
                aria-label="Eliminar orden de pago"
                title="Eliminar"
              >
                <IconTrash />
              </button>
            )}
          </div>
          <div className="footer-actions">
            <button type="button" className="btn secondary" onClick={onClose} disabled={saving}>Cancelar</button>
            <button type="submit" form="deposit-modal-form" className="btn" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}
    >
      <form id="deposit-modal-form" className="modal-form" onSubmit={handleSubmit}>
        <div className="form-grid two-columns">
          <label>
            Fecha
            <input type="date" name="date" value={formData.date} onChange={handleChange} required />
          </label>
          <label>
            Tipo
            <select name="type" value={formData.type} onChange={handleChange} required>
              <option value="" disabled>Seleccionar...</option>
              {DEPOSIT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Moneda
            <select name="currency" value={formData.currency} onChange={handleChange} disabled>
              <option value="$">$</option>
              <option value="USD">USD</option>
            </select>
          </label>
          <label>
            Importe
            <input type="number" step="0.01" name="value" value={formData.value} onChange={handleChange} required />
          </label>
          <label>
            Comprobante
            <input name="receipt" value={formData.receipt} onChange={handleChange} />
          </label>
        </div>

        {error && (
          <div className="form-error-message" role="alert">
            {error}
          </div>
        )}

      </form>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Eliminar orden de pago"
        message="¿Está seguro que desea eliminar esta orden de pago?"
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        onConfirm={async () => {
          setSaving(true);
          setError('');
          try {
            await onDelete?.(initialData?.id);
            setConfirmOpen(false);
          } catch (err) {
            console.error(err);
            setError(err?.message || 'Error al eliminar la orden de pago');
          } finally {
            setSaving(false);
          }
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </Modal>
  );
};

export default DepositModal;

